import type { CoreData, NameEntry, SexIdx, SexPref } from "./types";
import { TAG } from "./types";

const nf = new Intl.NumberFormat("en-GB");

export function fmt(n: number): string {
  return nf.format(n);
}

export function compact(n: number): string {
  if (n < 1000) return String(n);
  if (n < 10_000) return (n / 1000).toFixed(1).replace(/\.0$/, "") + "K";
  if (n < 1_000_000) return Math.round(n / 1000) + "K";
  return (n / 1_000_000).toFixed(1).replace(/\.0$/, "") + "M";
}

/** Which sex's statistics to show for this name, given the deck the user is browsing. */
export function sexIdxFor(e: NameEntry, pref: SexPref): SexIdx {
  if (pref === "F" && e.g !== "M") return 0;
  if (pref === "M" && e.g !== "F") return 1;
  if (e.g === "F") return 0;
  if (e.g === "M") return 1;
  return e.fs >= 50 ? 0 : 1;
}

export const SEX_WORD = ["girls", "boys"] as const;
export const SEX_NOUN = ["Girl", "Boy"] as const;

export function genderLabel(e: NameEntry): string {
  if (e.tags & TAG.UNISEX) return "Unisex";
  if (e.g === "B") return e.fs >= 50 ? "Mostly girls" : "Mostly boys";
  return e.g === "F" ? "Girl" : "Boy";
}

export function genderClass(e: NameEntry): "girl" | "boy" | "unisex" {
  if (e.tags & TAG.UNISEX) return "unisex";
  return e.fs >= 50 ? "girl" : "boy";
}

export function rankTier(rank: number): string {
  if (!rank) return "Rare";
  if (rank <= 10) return "Top 10";
  if (rank <= 100) return "Top 100";
  if (rank <= 500) return "Top 500";
  if (rank <= 1000) return "Top 1,000";
  if (rank <= 5000) return "Uncommon";
  return "Rare";
}

export interface TrendInfo {
  label: string;
  dir: "up" | "down" | "flat" | "none";
  strong: boolean;
}

export function trendInfo(pct: number | null): TrendInfo {
  if (pct === null) return { label: "Too rare to call", dir: "none", strong: false };
  if (pct >= 999) return { label: "Newly popular", dir: "up", strong: true };
  if (pct >= 100) return { label: "Rising fast", dir: "up", strong: true };
  if (pct >= 25) return { label: "Rising", dir: "up", strong: false };
  if (pct > -25) return { label: "Steady", dir: "flat", strong: false };
  if (pct > -60) return { label: "Falling", dir: "down", strong: false };
  return { label: "Falling fast", dir: "down", strong: true };
}

/** "1 in 85" — births of that sex in the latest year divided by this name's count. */
export function oneIn(core: CoreData, e: NameEntry, si: SexIdx): string | null {
  const count = e.c[si];
  if (!count) return null;
  const births = (si === 0 ? core.births.F : core.births.M)[core.latest - core.y0];
  const ratio = births / count;
  if (ratio < 10) return ratio.toFixed(1);
  const mag = Math.pow(10, Math.floor(Math.log10(ratio)) - 1);
  return fmt(Math.round(ratio / mag) * mag);
}

/**
 * Chance that a child shares their name with at least one other child of the same sex in a
 * class of 30 (about 15 of each), using the latest year's share of births.
 */
export function classChance(core: CoreData, e: NameEntry, si: SexIdx): number | null {
  const count = e.c[si];
  if (!count) return null;
  const births = (si === 0 ? core.births.F : core.births.M)[core.latest - core.y0];
  const p = count / births;
  return 1 - Math.pow(1 - p, 14);
}

/** a share as people say it: "62%", but "under 1%" and "over 99%" near the ends */
export function share(x: number): string {
  if (x > 0 && x < 0.01) return "under 1%";
  if (x < 1 && x > 0.99) return "over 99%";
  return `${Math.round(x * 100)}%`;
}

export function percent(x: number): string {
  if (x < 0.01) return "under 1%";
  if (x < 0.1) return `${(x * 100).toFixed(1).replace(/\.0$/, "")}%`;
  return `${Math.round(x * 100)}%`;
}

/** "+34% in 5 yrs" style sub-label for the trend figure. */
export function trendChange(pct: number | null): string {
  if (pct === null) return "five-year trend";
  if (pct >= 999) return "from almost none";
  if (pct === 0) return "no change in 5 yrs";
  return `${pct > 0 ? "+" : "−"}${fmt(Math.abs(pct))}% in 5 yrs`;
}

export function rankChange(e: NameEntry, si: SexIdx): number | null {
  const now = e.r[si];
  const then = e.r5[si];
  if (!now || !then) return null;
  return then - now;
}

/** 1st, 2nd, 3rd, 11th, 1,002nd */
export function ordinal(n: number): string {
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return fmt(n) + (s[(v - 20) % 10] || s[v] || s[0]);
}

export const B64 = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_";

/** Decode a sparkline string to 0..1 values (sqrt-scaled at build time; squared back here). */
export function decodeSpark(s: string): number[] {
  const out: number[] = [];
  for (const ch of s) {
    const q = B64.indexOf(ch) / 63;
    out.push(q * q);
  }
  return out;
}

export function initialsOf(...parts: (string | undefined)[]): string {
  return parts
    .filter((p): p is string => !!p && !!p.trim())
    .map((p) => p.trim().charAt(0).toUpperCase())
    .join("");
}

/** Initials that spell something you might not want on a monogrammed towel. */
const AWKWARD = new Set([
  "ASS", "BUM", "COW", "DIE", "DUD", "FAT", "FU", "GAY", "HAG", "KKK", "LOL", "OMG", "PIG",
  "POO", "PMS", "RAT", "SAD", "SOS", "STD", "SUX", "TIT", "UGH", "WTF", "FML", "BRA", "ICK",
  "FAG", "NAZ", "NUT", "SOB", "BO", "PEE", "WEE", "ZIT", "DUM", "MAD", "BAD", "SIN", "HOG",
  "SAG", "FUK", "VD", "IUD", "BUG", "DOG", "APE", "GOD", "IOU", "VAT", "NHS", "MOT", "TBH",
]);

export function awkwardInitials(initials: string): boolean {
  return AWKWARD.has(initials.toUpperCase());
}

/** Parses YYYY-MM-DD as a local date, or null. */
export function localDate(ymd: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd);
  if (!m) return null;
  const d = new Date(2000, 0, 1);
  // setFullYear, as the Date constructor reads years below 100 as 19xx
  d.setFullYear(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return d.getMonth() === Number(m[2]) - 1 && d.getDate() === Number(m[3]) ? d : null;
}

/** "3 March 2027", or "March 3, 2027" in the US */
export function longDate(ymd: string, us = false): string {
  const d = localDate(ymd);
  return d ? d.toLocaleDateString(us ? "en-US" : "en-GB", { day: "numeric", month: "long", year: "numeric" }) : "";
}

/** "04:12", or "4:12 AM" in the US */
export function clockTime(hm: string, us = false): string {
  const m = /^(\d{2}):(\d{2})$/.exec(hm);
  if (!m || !us) return hm;
  const h = Number(m[1]);
  return `${h % 12 || 12}:${m[2]} ${h < 12 ? "AM" : "PM"}`;
}
